// front/src/components/TodoCreateModal.jsx
import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { getTodoCibles, createTodo, getParamDefTodo } from '../api/todos';
import { lookupPassagePii } from '../api/patients';
import CibleSelector from './CibleSelector';

const piiFieldConfig = {
  nom: {
    label: 'Nom',
    type: 'text',
    placeholder: 'Nom',
  },
  prenom: {
    label: 'Prénom',
    type: 'text',
    placeholder: 'Prénom',
  },
  date_naissance: {
    label: 'Date de naissance',
    type: 'date',
    placeholder: '',
  },
  sexe: {
    label: 'Sexe',
    type: 'select',
  },
};

const entryFieldConfig = {
  passageUid: {
    label: 'Passage UID',
    type: 'text',
    placeholder: 'Passage UID',
  },
  comment: {
    label: 'Commentaire',
    type: 'text',
    placeholder: 'Commentaire',
  },
  adressTo: {
    label: 'Réadressage vers',
    type: 'select',
  },
  urgence: {
    label: 'Urgence',
    type: 'checkbox',
  },
};

const lookupFieldConfig = {
  nom: {
    label: 'Nom',
    type: 'text',
    placeholder: 'Nom',
  },
  prenom: {
    label: 'Prénom',
    type: 'text',
    placeholder: 'Prénom',
  },
  date_naissance: {
    label: 'Date de naissance',
    type: 'date',
    placeholder: '',
  },
};

export default function TodoCreateModal({ open, onClose, profilDemandeurId, onCreated }) {
  const [paramDefTodo, setParamDefTodo] = useState(null);
  const [paramsDefTodos, setParamsDefTodos] = useState({});
  const [todo, setTodo] = useState({});
  const [lookupParams, setLookupParams] = useState({});
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupHasSearched, setLookupHasSearched] = useState(false);

  const [etabs, setEtabs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cibles, setCibles] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr('');
    setCibles([]);
    setTodo({});
    (async () => {
      try {
        const [tg, pdt] = await Promise.all([getTodoCibles(), getParamDefTodo()]);
        setParamsDefTodos(pdt);
        setEtabs(tg.etablissements || []);
        setRoles(tg.roles || []);
        if (!(tg.etablissements || []).length) {
          setErr(
            'Aucun établissement disponible pour votre compte (entité manquante ?).  Impossible de créer une To Do.',
          );
        }
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!paramDefTodo) return;

    setLookupParams({});
    setLookupResults([]);
    setLookupHasSearched(false);

    setTodo((prevTodo) => {
      const oldEntryTodoPii = prevTodo.entryTodoPii || {};
      const newEntryTodoPii = {};

      paramDefTodo.entryToDoPII.forEach((field) => {
        newEntryTodoPii[field] = oldEntryTodoPii[field] ?? '';
      });

      return {
        ...prevTodo,
        entryTodoPii: newEntryTodoPii,
      };
    });
  }, [paramDefTodo]);

  const submit = async () => {
    try {
      setErr('');
      if (!profilDemandeurId) throw new Error('Profil actif introuvable');
      if (!cibles.length) throw new Error('Ajoutez au moins une cible');

      setLoading(true);
      const payload = { todo, cibles, profilDemandeurId, paramDefTodo };
      const newTodo = await createTodo(payload);
      onCreated?.(newTodo);
      onClose?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Nouvelle To Do"
      onClose={onClose}
      actions={
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border px-3 py-2 text-sm hover:bg-slate-50"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={loading}
            className="rounded-lg bg-[#A51A1F] text-white px-3 py-2 text-sm disabled:opacity-60"
          >
            {loading ? 'Envoi…' : 'Créer'}
          </button>
        </div>
      }
    >
      {err && <div className="mb-3 text-sm text-red-600">{err}</div>}

      <div className="space-y-4">
        <div>
          <label className="block text-sm text-slate-600 mb-1">Type</label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={paramDefTodo?.key ?? ''}
            onChange={(e) => {
              const key = e.target.value;

              setParamDefTodo(
                key
                  ? {
                      key,
                      ...paramsDefTodos[key],
                    }
                  : null,
              );
            }}
          >
            <option value="">-- Sélectionner --</option>

            {Object.entries(paramsDefTodos)
              .sort(([, a], [, b]) => a.orderInMenu - b.orderInMenu)
              .map(([key, element]) => (
                <option key={key} value={key}>
                  {element.name}
                </option>
              ))}
          </select>
        </div>

        {paramDefTodo?.entryToDoPII?.map((field) => {
          const config = piiFieldConfig[field];

          if (!config) return null;

          return (
            <div key={field}>
              <label className="block text-sm text-slate-600 mb-1">{config.label}</label>

              {field === 'sexe' ? (
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={todo.entryTodoPii?.[field] ?? ''}
                  onChange={(e) =>
                    setTodo((prev) => ({
                      ...prev,
                      entryTodoPii: {
                        ...prev.entryTodoPii,
                        [field]: e.target.value,
                      },
                    }))
                  }
                >
                  <option value="">-- Sélectionner --</option>

                  {paramDefTodo.dropdowns?.sexe?.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={config.type}
                  className="w-full border rounded-lg px-3 py-2"
                  value={todo.entryTodoPii?.[field] ?? ''}
                  onChange={(e) =>
                    setTodo((prev) => ({
                      ...prev,
                      entryTodoPii: {
                        ...prev.entryTodoPii,
                        [field]: e.target.value,
                      },
                    }))
                  }
                  placeholder={config.placeholder}
                />
              )}
            </div>
          );
        })}

        {paramDefTodo?.entry
          ?.filter((field) => field !== 'passageUid')
          .map((field) => {
            const config = entryFieldConfig[field];

            if (!config) return null;

            return (
              <div key={field}>
                {config.type === 'checkbox' ? (
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={todo.entry?.[field] ?? false}
                      onChange={(e) =>
                        setTodo((prev) => ({
                          ...prev,
                          entry: {
                            ...prev.entry,
                            [field]: e.target.checked,
                          },
                        }))
                      }
                    />

                    {config.label}
                  </label>
                ) : config.type === 'select' ? (
                  <>
                    <label className="block text-sm text-slate-600 mb-1">{config.label}</label>

                    <select
                      className="w-full border rounded-lg px-3 py-2"
                      value={todo.entry?.[field] ?? ''}
                      onChange={(e) =>
                        setTodo((prev) => ({
                          ...prev,
                          entry: {
                            ...prev.entry,
                            [field]: e.target.value,
                          },
                        }))
                      }
                    >
                      <option value="">-- Sélectionner --</option>

                      {paramDefTodo.dropdowns?.[field]?.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <>
                    <label className="block text-sm text-slate-600 mb-1">{config.label}</label>

                    <input
                      type={config.type}
                      className="w-full border rounded-lg px-3 py-2"
                      value={todo.entry?.[field] ?? ''}
                      onChange={(e) =>
                        setTodo((prev) => ({
                          ...prev,
                          entry: {
                            ...prev.entry,
                            [field]: e.target.value,
                          },
                        }))
                      }
                      placeholder={config.placeholder}
                    />
                  </>
                )}
              </div>
            );
          })}

        {paramDefTodo?.entry?.includes('passageUid') && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-600 mb-1">Passage UID</label>

              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2"
                value={todo.entry?.passageUid ?? ''}
                onChange={(e) =>
                  setTodo((prev) => ({
                    ...prev,
                    entry: {
                      ...prev.entry,
                      passageUid: e.target.value,
                    },
                  }))
                }
                placeholder="Passage UID"
              />
            </div>

            {paramDefTodo.lookupPassage_PII?.length > 0 && (
              <details className="border border-slate-200 rounded-lg bg-slate-50">
                <summary className="cursor-pointer list-none px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700">
                        Rechercher un passage
                      </h3>

                      <p className="text-xs text-slate-500 mt-1">
                        Rechercher un passage à partir de ses informations personnelles
                      </p>
                    </div>

                    <span className="text-slate-400 text-sm">▼</span>
                  </div>
                </summary>

                <div className="border-t border-slate-200 p-4 space-y-4">
                  <div className="space-y-4">
                    {paramDefTodo.lookupPassage_PII.map((field) => {
                      const config = lookupFieldConfig[field];

                      if (!config) return null;

                      return (
                        <div key={field}>
                          <label className="block text-sm text-slate-600 mb-1">
                            {config.label}
                          </label>

                          <input
                            type={config.type}
                            className="w-full border rounded-lg px-3 py-2 bg-white"
                            value={lookupParams[field] ?? ''}
                            onChange={(e) =>
                              setLookupParams((prev) => ({
                                ...prev,
                                [field]: e.target.value,
                              }))
                            }
                            placeholder={config.placeholder}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="border border-slate-300 bg-white rounded-lg px-4 py-2 text-sm hover:bg-slate-100 disabled:opacity-50"
                    disabled={lookupLoading}
                    onClick={async () => {
                      setLookupLoading(true);
                      setLookupHasSearched(false);

                      try {
                        const results = await lookupPassagePii(lookupParams);

                        setLookupResults(results ?? []);
                        setLookupHasSearched(true);
                      } catch (error) {
                        console.error(error);
                        setLookupResults([]);
                        setLookupHasSearched(true);
                      } finally {
                        setLookupLoading(false);
                      }
                    }}
                  >
                    {lookupLoading ? 'Recherche...' : 'Rechercher le passage'}
                  </button>

                  {lookupHasSearched && !lookupLoading && lookupResults.length === 0 && (
                    <div className="text-sm text-slate-500 border border-slate-200 rounded-lg bg-white px-3 py-2">
                      Aucun passage trouvé.
                    </div>
                  )}

                  {lookupResults.length > 0 && (
                    <div className="border border-slate-200 rounded-lg bg-white divide-y">
                      <div className="px-3 py-2 text-xs font-medium text-slate-500 bg-slate-50">
                        Passages trouvés
                      </div>

                      {lookupResults.map((passage) => (
                        <button
                          key={passage.passage_uid}
                          type="button"
                          className="w-full text-left px-3 py-3 hover:bg-slate-50"
                          onClick={() => {
                            setTodo((prev) => ({
                              ...prev,
                              entry: {
                                ...prev.entry,
                                passageUid: passage.passage_uid,
                              },
                            }));

                            setLookupResults([]);
                            setLookupHasSearched(false);
                          }}
                        >
                          <div className="font-medium text-slate-700">
                            {passage.prenom} {passage.nom}
                          </div>

                          <div className="text-sm text-slate-500">{passage.date_naissance}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm text-slate-600 mb-1">Cibles</label>
          <CibleSelector roles={roles} etabs={etabs} value={cibles} onChange={setCibles} />
        </div>

        {/*===========*/}
      </div>

      <div className="space-y-4 bg-red-600 mt-5 hidden"></div>
    </Modal>
  );
}
